import * as React from "react";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Check, FileText, FileType2, Loader2, Search, Upload, X } from "lucide-react";
import { cn } from "../../lib/cn";
import { AndoLogo } from "../AndoLogo";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../primitives/tabs";

/**
 * StyleTemplatePicker — "scegli come vuoi che venga creato" control fused
 * into the ``document_create`` proposal card (and re-used as the settings
 * gallery via the exported {@link StyleSwatch}).
 *
 * TWO mutually-exclusive modes live in one Tabs control:
 *
 *   - **Stile** — pick a built-in visual style preset. Each row renders the
 *     style name + a lightweight 3-stop colour SWATCH (surface / ink /
 *     accent) so the user gets a gallery preview without a heavy thumbnail.
 *     The implicit "Ando (default)" row sits first; selecting it clears the
 *     explicit pick (``styleId = undefined``) so the BE applies its default.
 *     Picking a style CLEARS any template (``templateSlug = undefined``).
 *
 *   - **Template** — pick a fixed-fill ``{{placeholder}}`` OOXML template.
 *     First-party Ando BUILTINS (badged "Ando") render at the TOP, then the
 *     user's own uploads. Picking a template CLEARS the style
 *     (``styleId = undefined``); template wins at execute time. The upload CTA
 *     shows when the user has no own uploads (builtins still render above).
 *
 * The value the parent threads through is intentionally minimal:
 * ``{ styleId?, templateSlug? }`` — exactly the two content meta-keys the
 * executor reads (``_style_id`` / ``_ooxml_template_slug``). The picker is
 * fully controlled; it owns NO data fetching (Storybook-friendly, mirrors
 * ``DestinationPicker``). Callers pass the already-fetched ``styles`` +
 * ``templates`` (via TanStack ``useStyles`` / ``listOoxmlTemplates``).
 *
 * a11y: each option is a real ``role="radio"`` inside a labelled
 * ``role="radiogroup"`` — arrow-key roamable, focus-ringed, the selected row
 * announced via ``aria-checked``. The AndoLogo is the AI signifier on the
 * default row (never Sparkles).
 */

// ── Public data shapes (decoupled from the apps-layer api module) ──────────

/** Coarse grouping rendered as a section header in the style list. */
export type StylePresetCategory =
  | "dark"
  | "light"
  | "warm"
  | "vivid"
  | "bw"
  | "mixed";

/**
 * A single style preset option. Hex fields are RRGGBB WITHOUT the leading
 * ``#`` (BE contract) — {@link StyleSwatch} prepends it.
 */
export interface StylePresetOption {
  readonly id: string;
  readonly name: string;
  readonly category: StylePresetCategory;
  readonly isDark: boolean;
  /** RRGGBB, no leading #. */
  readonly accent: string;
  /** RRGGBB, no leading #. */
  readonly ink: string;
  /** RRGGBB, no leading #. */
  readonly surface: string;
  /**
   * True when this style was LEARNED from a user-uploaded reference file
   * (``POST /api/v1/styles/learn``) rather than shipped as a built-in. Learned
   * styles render first, grouped under "I tuoi stili".
   */
  readonly isLearned?: boolean;
  /**
   * Rendered mini-cover PNG (cookie-authed ``<img>``) showing the style's real
   * grammar (chip + display type + accent bar + KPI card). When present the
   * row shows it instead of the 3-stop swatch.
   */
  readonly thumbnailUrl?: string;
}

/** A fixed-fill OOXML template option (first-party builtin or user upload). */
export interface OoxmlTemplateOption {
  readonly slug: string;
  /** Optional display label; falls back to the slug when absent. */
  readonly name?: string;
  /** Optional one-liner shown under the title. */
  readonly description?: string;
  /** ``"user"`` / ``"org"`` — badged so org-shared rows are distinguishable. */
  readonly scope?: string;
  /** ``document`` / ``spreadsheet`` / ``presentation`` — used for filtering. */
  readonly documentType?: string | null;
  /**
   * True for first-party Ando BUILTIN templates — rendered at the TOP of the
   * list with an "Ando" badge + AndoLogo (never Sparkles), ahead of uploads.
   */
  readonly isBuiltin?: boolean;
  /**
   * Absolute URL of the slide-1 PNG preview (cookie-authed `<img>` — the BE
   * renders + caches it). Absent ⇒ the card shows a graceful fallback tile.
   */
  readonly thumbnailUrl?: string;
  /**
   * TEST-ONLY curated template (e.g. SlidesGo) — badged so it's clear it must
   * be removed before production (licensing). First-party Ando decks omit it.
   */
  readonly testOnly?: boolean;
  /**
   * The AI tokenizer made this customer deck merge-ready (its content shapes
   * carry ``{{key}}`` placeholders + a per-slide schema). Generation fills it
   * deterministically via merge, preserving the design 100%. Badged so the user
   * sees their uploaded brand deck is ready to reuse.
   */
  readonly mergeReady?: boolean;
}

/**
 * The user's BRAND KIT default — the single template the BE applies when the
 * proposal carries no explicit style/template (``user → org → builtin Ando``).
 * It is otherwise INVISIBLE in the picker: surfacing it as the FIRST,
 * pre-selected card makes the actual default tangible (name + format + a
 * lightweight palette preview) instead of a silent fallback.
 *
 * Selecting it is the canonical DEFAULT selection — it emits NOTHING into the
 * action ``modifications`` (``{ styleId: undefined, templateSlug: undefined }``)
 * so the BE applies the kit. The card is purely informational + the anchor for
 * "I'm using my Brand Kit".
 */
export interface BrandKitDefaultOption {
  /** Display name of the effective kit (e.g. "Brand Kit Acme" / "Ando"). */
  readonly name: string;
  /**
   * True when the kit carries an uploaded OOXML template merged on top at
   * generation. False ⇒ the kit only themes (palette/fonts) the generated file.
   */
  readonly hasTemplate: boolean;
  /** RRGGBB (with or without leading #) — the kit accent, for the preview. */
  readonly accent?: string;
  /** RRGGBB (with or without leading #) — the kit ink, for the preview. */
  readonly ink?: string;
  /** RRGGBB (with or without leading #) — the kit surface, for the preview. */
  readonly surface?: string;
  /** True when the kit's palette is dark (affects the preview borders). */
  readonly isDark?: boolean;
  /**
   * Rendered mini-cover of the effective kit (``/brand-kits/_effective/thumbnail``)
   * — shown on the DEFAULT row of the Style tab so the customer sees THEIR
   * palette/typography, not a generic Ando swatch.
   */
  readonly thumbnailUrl?: string;
}

/** The minimal selection the parent threads into the action content. */
export interface StyleTemplateValue {
  /** Chosen style preset id (``content._style_id``). Absent ⇒ Ando default. */
  readonly styleId?: string;
  /** Chosen uploaded template slug (``content._ooxml_template_slug``). */
  readonly templateSlug?: string;
}

export interface StyleTemplatePickerProps {
  readonly value: StyleTemplateValue;
  readonly onChange: (next: StyleTemplateValue) => void;
  readonly styles: ReadonlyArray<StylePresetOption>;
  readonly templates: ReadonlyArray<OoxmlTemplateOption>;
  /**
   * The user's effective BRAND KIT default — rendered as the FIRST, pre-selected
   * card in the Template tab with a "Predefinito" badge. ``undefined`` while the
   * kit is loading (pair with {@link brandKitLoading}); ``null`` when there is no
   * kit at all (the empty state CTA shows). Selecting the card is the default
   * (no-modifications) pick — see {@link BrandKitDefaultOption}.
   */
  readonly brandKit?: BrandKitDefaultOption | null;
  /** True while the Brand Kit is still loading (renders a skeleton card). */
  readonly brandKitLoading?: boolean;
  /** True while the style list is still loading (renders a skeleton row). */
  readonly stylesLoading?: boolean;
  /** True while the template list is still loading. */
  readonly templatesLoading?: boolean;
  /**
   * Filter both lists to a single document type
   * (``document`` / ``spreadsheet`` / ``presentation``). Styles have no type
   * (every preset works for every doc), so this only narrows templates.
   */
  readonly documentType?: string | null;
  /** Open the settings page from the templates empty state. */
  readonly onOpenTemplateSettings?: () => void;
  /**
   * Upload a brand template (.pptx/.docx/.xlsx) inline from the gallery. When
   * provided, the gallery shows an upload tile; the parent owns the mutation
   * (``uploadOoxmlTemplate``) + cache invalidation, and on success the new
   * template appears under "I tuoi template". Omit to hide inline upload
   * (the empty-state still links to settings via ``onOpenTemplateSettings``).
   */
  readonly onUploadTemplate?: (file: File) => void;
  /** True while an inline template upload is in flight. */
  readonly uploadTemplatePending?: boolean;
  /**
   * Learn a brand-new style from a reference file the user drops on the
   * "Carica un riferimento" control in the Stile tab. When omitted the upload
   * affordance is hidden (e.g. read-only surfaces). The parent owns the
   * mutation (``learnStyleFromSample``) + cache invalidation; on success the
   * new style appears here under "I tuoi stili" and should be auto-selected.
   */
  readonly onLearnStyle?: (file: File) => void;
  /** True while a learn-from-reference upload is in flight. */
  readonly learnStylePending?: boolean;
  /** Last learn-from-reference error message (shown under the dropzone). */
  readonly learnStyleError?: string | null;
  /** Disable all controls while the parent's submit is in-flight. */
  readonly disabled?: boolean;
  readonly className?: string;
}

const CATEGORY_LABEL: Record<StylePresetCategory, MessageDescriptor> = {
  dark: msg`Scuri`,
  light: msg`Chiari`,
  warm: msg`Caldi`,
  vivid: msg`Vivaci`,
  bw: msg`Bianco e nero`,
  mixed: msg`Misti`,
};

// Stable section order so the list never reshuffles between renders.
const CATEGORY_ORDER: ReadonlyArray<StylePresetCategory> = [
  "warm",
  "light",
  "dark",
  "vivid",
  "bw",
  "mixed",
];

/** Normalise a BE hex (``"RRGGBB"`` or ``"#RRGGBB"``) into a CSS colour. */
function toCssHex(hex: string): string {
  const trimmed = hex.trim();
  return trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
}

// ── StyleSwatch — shared gallery preview (picker rows + settings) ──────────

export interface StyleSwatchProps {
  readonly surface: string;
  readonly ink: string;
  readonly accent: string;
  readonly isDark?: boolean;
  /** Pixel height of the swatch. Default 36. */
  readonly size?: number;
  readonly className?: string;
}

/**
 * A compact, dependency-free preview of a style: the ``surface`` fills the
 * card, a stub of ``ink`` text + an ``accent`` pill float on top. Renders
 * the gist of a slide/page WITHOUT a heavy server thumbnail. Re-used by both
 * the picker rows and the settings gallery (DRY).
 */
export const StyleSwatch: React.FC<StyleSwatchProps> = ({
  surface,
  ink,
  accent,
  isDark,
  size = 36,
  className,
}) => {
  const surfaceCss = toCssHex(surface);
  const inkCss = toCssHex(ink);
  const accentCss = toCssHex(accent);
  return (
    <span
      aria-hidden
      className={cn(
        "relative inline-flex shrink-0 flex-col justify-center gap-1 overflow-hidden rounded-md border px-2",
        isDark ? "border-white/15" : "border-black/10",
        className,
      )}
      style={{
        backgroundColor: surfaceCss,
        width: Math.round(size * 1.55),
        height: size,
      }}
    >
      {/* Title stub (ink) */}
      <span
        className="block h-1.5 w-[70%] rounded-full"
        style={{ backgroundColor: inkCss, opacity: 0.85 }}
      />
      {/* Body stub (ink, dimmer) */}
      <span
        className="block h-1 w-[90%] rounded-full"
        style={{ backgroundColor: inkCss, opacity: 0.4 }}
      />
      {/* Accent pill — the brand/highlight colour */}
      <span
        className="block h-1.5 w-[34%] rounded-full"
        style={{ backgroundColor: accentCss }}
      />
    </span>
  );
};
StyleSwatch.displayName = "StyleSwatch";

// ── Selectable row (shared between style + template lists) ─────────────────

interface PickRowProps {
  readonly selected: boolean;
  readonly title: string;
  readonly subtitle?: React.ReactNode;
  readonly leading: React.ReactNode;
  readonly trailing?: React.ReactNode;
  readonly ariaLabel: string;
  readonly disabled?: boolean;
  readonly onSelect: () => void;
}

const PickRow: React.FC<PickRowProps> = ({
  selected,
  title,
  subtitle,
  leading,
  trailing,
  ariaLabel,
  disabled,
  onSelect,
}) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    aria-label={ariaLabel}
    disabled={disabled}
    // Roving tabindex: only the selected row is in the tab order; the rest
    // are reachable via arrow keys (handled by the radiogroup container).
    tabIndex={selected ? 0 : -1}
    onClick={onSelect}
    className={cn(
      "group flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-2 text-left transition-colors duration-150",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange focus-visible:ring-offset-1",
      "disabled:cursor-not-allowed disabled:opacity-50",
      selected
        ? "border-ando-orange bg-ando-orange-soft/40"
        : "border-hairline bg-white/60 hover:border-ando-orange/40 hover:bg-ando-orange-soft/15",
    )}
  >
    <span className="shrink-0">{leading}</span>
    <span className="min-w-0 flex-1">
      <span className="block truncate font-medium text-[12.5px] text-ink-1">
        {title}
      </span>
      {subtitle ? (
        <span className="mt-0.5 block truncate text-[11px] text-ink-3">
          {subtitle}
        </span>
      ) : null}
    </span>
    {trailing ? <span className="shrink-0">{trailing}</span> : null}
    <span
      aria-hidden
      className={cn(
        "grid h-4 w-4 shrink-0 place-items-center rounded-full border transition-colors",
        selected
          ? "border-ando-orange bg-ando-orange text-white"
          : "border-hairline text-transparent",
      )}
    >
      <Check className="h-3 w-3" strokeWidth={3} />
    </span>
  </button>
);
PickRow.displayName = "StyleTemplatePicker.PickRow";

// ── Arrow-key roving for a radiogroup of PickRows ──────────────────────────

function useRovingRadioGroup(disabled?: boolean) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const onKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;
      if (
        e.key !== "ArrowDown" &&
        e.key !== "ArrowUp" &&
        e.key !== "ArrowRight" &&
        e.key !== "ArrowLeft"
      ) {
        return;
      }
      const root = ref.current;
      if (!root) return;
      const items = Array.from(
        root.querySelectorAll<HTMLButtonElement>(
          'button[role="radio"]:not(:disabled)',
        ),
      );
      if (items.length === 0) return;
      const current = document.activeElement as HTMLElement | null;
      const idx = items.findIndex((el) => el === current);
      const forward = e.key === "ArrowDown" || e.key === "ArrowRight";
      const nextIdx =
        idx < 0
          ? 0
          : (idx + (forward ? 1 : -1) + items.length) % items.length;
      e.preventDefault();
      items[nextIdx]?.focus();
      // Move selection with focus, matching native radiogroup behaviour.
      items[nextIdx]?.click();
    },
    [disabled],
  );
  return { ref, onKeyDown };
}

// ── Debounce (keeps the 52-style filter snappy without re-filtering on every
//    keystroke) ──────────────────────────────────────────────────────────────

function useDebouncedValue<T>(value: T, delayMs = 150): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

// ── Style list ─────────────────────────────────────────────────────────────

/** Rendered mini-cover leading a pick row — ONE markup for the style rows and
 * the Brand Kit default row (cookie-authed <img>, DRY). */
const RowThumb: React.FC<{ readonly src: string }> = ({ src }) => (
  <img
    src={src}
    alt=""
    aria-hidden
    loading="lazy"
    className="h-10 w-[71px] shrink-0 rounded-md border border-hairline object-cover"
  />
);

interface StyleListProps {
  readonly styles: ReadonlyArray<StylePresetOption>;
  readonly loading?: boolean;
  readonly selectedId?: string;
  readonly disabled?: boolean;
  readonly onSelect: (styleId?: string) => void;
  /** Learn-from-reference handler; when omitted the dropzone is hidden. */
  readonly onLearnStyle?: (file: File) => void;
  readonly learnStylePending?: boolean;
  readonly learnStyleError?: string | null;
  /**
   * The effective Brand Kit — personalizes the DEFAULT row (name + rendered
   * mini-cover) since the no-pick default IS the kit at generation time.
   */
  readonly brandKit?: BrandKitDefaultOption | null;
}

const StyleList: React.FC<StyleListProps> = ({
  styles,
  loading,
  selectedId,
  disabled,
  onSelect,
  onLearnStyle,
  learnStylePending,
  learnStyleError,
  brandKit,
}) => {
  const { i18n } = useLingui();
  const { ref, onKeyDown } = useRovingRadioGroup(disabled);

  // Free-text filter over the (potentially 50+) presets. Debounced so a
  // non-technical user can scan/narrow the gallery by name or category label
  // without re-filtering on every keystroke. Matches the style NAME or its
  // localised category label (e.g. "scuri", "caldi").
  const [query, setQuery] = React.useState("");
  const debouncedQuery = useDebouncedValue(query, 150);
  const normalizedQuery = debouncedQuery.trim().toLowerCase();

  const filteredStyles = React.useMemo(() => {
    if (!normalizedQuery) return styles;
    return styles.filter((s) => {
      const descriptor = CATEGORY_LABEL[s.category];
      const label = descriptor ? i18n._(descriptor) : "";
      return (
        s.name.toLowerCase().includes(normalizedQuery) ||
        label.toLowerCase().includes(normalizedQuery)
      );
    });
  }, [styles, normalizedQuery, i18n]);

  // Learned styles (user-uploaded references) render FIRST under "I tuoi
  // stili"; the curated built-ins follow, grouped by category in
  // CATEGORY_ORDER (unknown categories sink to the end under "Misti"). The
  // default row lives above all groups.
  const learned = React.useMemo(
    () => filteredStyles.filter((s) => s.isLearned),
    [filteredStyles],
  );
  const grouped = React.useMemo(() => {
    const buckets = new Map<StylePresetCategory, StylePresetOption[]>();
    for (const s of filteredStyles) {
      if (s.isLearned) continue;
      const key = CATEGORY_ORDER.includes(s.category) ? s.category : "mixed";
      const arr = buckets.get(key) ?? [];
      arr.push(s);
      buckets.set(key, arr);
    }
    return CATEGORY_ORDER.map((cat) => ({
      category: cat,
      items: buckets.get(cat) ?? [],
    })).filter((g) => g.items.length > 0);
  }, [filteredStyles]);

  const isDefault = !selectedId;
  const hasQuery = normalizedQuery.length > 0;
  const noMatches = hasQuery && learned.length === 0 && grouped.length === 0;
  const searchInputId = React.useId();

  const renderStyleRow = (s: StylePresetOption) => (
    <PickRow
      key={s.id}
      selected={selectedId === s.id}
      title={s.name}
      ariaLabel={i18n._(msg`Stile ${s.name}`)}
      disabled={disabled}
      onSelect={() => onSelect(s.id)}
      leading={
        s.thumbnailUrl ? (
          <RowThumb src={s.thumbnailUrl} />
        ) : (
          <StyleSwatch
            surface={s.surface}
            ink={s.ink}
            accent={s.accent}
            isDark={s.isDark}
          />
        )
      }
      trailing={
        s.isLearned ? (
          <span className="rounded-full border border-ando-orange/30 bg-ando-orange-soft px-1.5 py-0.5 font-medium text-[9.5px] text-ando-orange uppercase tracking-wide">
            {i18n._(msg`Tuo`)}
          </span>
        ) : undefined
      }
    />
  );

  return (
    <div className="flex flex-col gap-2.5">
      {/* Search/filter — a non-technical user can scan 50+ presets by name or
          category. Hidden while loading or when there is nothing to filter. */}
      {!loading && styles.length > 0 ? (
        <div className="relative">
          <Search
            className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 text-ink-3"
            aria-hidden
          />
          <input
            id={searchInputId}
            type="search"
            inputMode="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            disabled={disabled}
            placeholder={i18n._(msg`Cerca uno stile…`)}
            aria-label={i18n._(msg`Cerca uno stile per nome o categoria`)}
            className={cn(
              "w-full rounded-lg border border-hairline bg-white/70 py-1.5 pr-8 pl-8 text-[12.5px] text-ink-1 placeholder:text-ink-3",
              "focus-visible:border-ando-orange focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/30",
              "disabled:cursor-not-allowed disabled:opacity-50",
            )}
          />
          {query ? (
            <button
              type="button"
              onClick={() => setQuery("")}
              disabled={disabled}
              aria-label={i18n._(msg`Cancella la ricerca`)}
              className="-translate-y-1/2 absolute top-1/2 right-2 grid h-5 w-5 place-items-center rounded-full text-ink-3 transition-colors hover:bg-cream-deep hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/40"
            >
              <X className="h-3 w-3" strokeWidth={2.5} aria-hidden />
            </button>
          ) : null}
        </div>
      ) : null}

      <div
        ref={ref}
        role="radiogroup"
        aria-label={i18n._(msg`Stile del documento`)}
        onKeyDown={onKeyDown}
        className="flex max-h-[320px] flex-col gap-3 overflow-y-auto pr-0.5"
      >
        {/* Learn-from-reference dropzone + default row are hidden while a search
            is active so the filtered results read cleanly. The AI signifier
            (AndoLogo) sells "drop a branded file and Ando learns its look".
            Hidden on read-only surfaces (no handler). */}
        {!hasQuery && onLearnStyle ? (
          <LearnStyleDropzone
            onPick={onLearnStyle}
            pending={learnStylePending}
            error={learnStyleError}
            disabled={disabled}
          />
        ) : null}

        {/* Brand Kit default — always first. The no-pick default IS the
            effective kit at generation time, so the row shows the kit's NAME
            and its rendered mini-cover (real palette/typography) instead of a
            generic Ando swatch. Falls back to the AndoLogo tile while the kit
            is loading or absent. */}
        {!hasQuery ? (
          <PickRow
            selected={isDefault}
            title={brandKit?.name ?? "Ando"}
            subtitle={
              brandKit
                ? i18n._(msg`Il tuo Brand Kit · ottimizzato dall'AI`)
                : i18n._(msg`Stile predefinito · ottimizzato dall'AI`)
            }
            ariaLabel={i18n._(msg`Stile ${brandKit?.name ?? "Ando"} (predefinito)`)}
            disabled={disabled}
            onSelect={() => onSelect(undefined)}
            leading={
              brandKit?.thumbnailUrl ? (
                <RowThumb src={brandKit.thumbnailUrl} />
              ) : (
                <span className="grid h-9 w-[3.4rem] place-items-center rounded-md border border-hairline bg-cream/60">
                  <AndoLogo size={18} />
                </span>
              )
            }
            trailing={
              <span className="rounded-full bg-ando-orange-soft px-1.5 py-0.5 font-medium text-[9.5px] text-ando-orange uppercase tracking-wide">
                {i18n._(msg`Default`)}
              </span>
            }
          />
        ) : null}

        {/* No-results state when a search matches nothing. */}
        {noMatches ? (
          <p
            role="status"
            className="px-1 py-6 text-center text-[12px] text-ink-3"
          >
            {i18n._(msg`Nessuno stile corrisponde a “${debouncedQuery.trim()}”.`)}
          </p>
        ) : null}

      {/* Learned styles — "I tuoi stili", above the curated built-ins. */}
      {learned.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <div className="px-1 font-medium text-[10px] text-ink-3 uppercase tracking-[0.06em]">
            {i18n._(msg`I tuoi stili`)}
          </div>
          {learned.map(renderStyleRow)}
        </div>
      ) : null}

      {loading ? (
        <div className="flex flex-col gap-1.5" aria-hidden>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-[52px] animate-pulse rounded-lg border border-hairline bg-cream-deep/30"
            />
          ))}
        </div>
      ) : (
        grouped.map((group) => (
          <div key={group.category} className="flex flex-col gap-1.5">
            <div className="px-1 font-medium text-[10px] text-ink-3 uppercase tracking-[0.06em]">
              {i18n._(CATEGORY_LABEL[group.category])}
            </div>
            {group.items.map(renderStyleRow)}
          </div>
        ))
      )}
      </div>
    </div>
  );
};
StyleList.displayName = "StyleTemplatePicker.StyleList";

// ── Learn-a-style-from-reference dropzone ──────────────────────────────────

export interface LearnStyleDropzoneProps {
  /** Called with the user's chosen reference file (.pptx/.docx/.xlsx). */
  readonly onPick: (file: File) => void;
  readonly pending?: boolean;
  readonly error?: string | null;
  readonly disabled?: boolean;
  readonly className?: string;
}

/**
 * Accessible drop-or-click control to learn a brand-new style from a branded
 * reference file. Shared by the picker (Stile tab) and the settings gallery
 * (DRY) so both expose the SAME affordance + drag/error plumbing. The AndoLogo
 * is the AI signifier ("Ando learns your look"), never Sparkles.
 */
export const LearnStyleDropzone: React.FC<LearnStyleDropzoneProps> = ({
  onPick,
  pending,
  error,
  disabled,
  className,
}) => {
  const { i18n } = useLingui();
  const inputId = React.useId();
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = React.useState(false);

  const blocked = Boolean(disabled || pending);

  const pickFromList = React.useCallback(
    (files: FileList | null) => {
      const file = files?.[0];
      if (file) onPick(file);
    },
    [onPick],
  );

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <label
        htmlFor={inputId}
        onDragOver={(e) => {
          if (blocked) return;
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          if (blocked) return;
          e.preventDefault();
          setDragging(false);
          pickFromList(e.dataTransfer.files);
        }}
        className={cn(
          "flex cursor-pointer items-center gap-2.5 rounded-lg border border-dashed px-3 py-2.5 text-left transition-colors",
          "focus-within:outline-none focus-within:ring-2 focus-within:ring-ando-orange focus-within:ring-offset-1",
          dragging
            ? "border-ando-orange bg-ando-orange-soft/40"
            : "border-ando-orange/40 bg-ando-orange-soft/10 hover:bg-ando-orange-soft/20",
          blocked && "cursor-not-allowed opacity-60",
        )}
      >
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md border border-hairline bg-cream/60">
          {pending ? (
            <Loader2 className="h-4 w-4 animate-spin text-ando-orange" aria-hidden />
          ) : (
            <AndoLogo size={18} />
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-medium text-[12.5px] text-ink-1">
            {pending ? i18n._(msg`Sto imparando lo stile…`) : i18n._(msg`Carica un riferimento`)}
          </span>
          <span className="mt-0.5 block text-[11px] text-ink-3">
            {i18n._(msg`Trascina o seleziona un .pptx / .docx / .xlsx — Ando ne impara il look.`)}
          </span>
        </span>
        <input
          id={inputId}
          ref={inputRef}
          type="file"
          accept=".pptx,.docx,.xlsx"
          className="sr-only"
          disabled={blocked}
          onChange={(e) => {
            pickFromList(e.target.files);
            // Reset so picking the SAME file twice still fires onChange.
            if (inputRef.current) inputRef.current.value = "";
          }}
        />
      </label>
      {error ? (
        <p
          role="alert"
          className="px-1 text-[11px] text-destructive"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
};
LearnStyleDropzone.displayName = "StyleTemplatePicker.LearnStyleDropzone";

// ── Template list ───────────────────────────────────────────────────────────

interface TemplateListProps {
  readonly templates: ReadonlyArray<OoxmlTemplateOption>;
  readonly loading?: boolean;
  readonly selectedSlug?: string;
  readonly disabled?: boolean;
  readonly onSelect: (slug?: string) => void;
  readonly onOpenSettings?: () => void;
  readonly onUpload?: (file: File) => void;
  readonly uploadPending?: boolean;
  /** The user's effective Brand Kit default (FIRST + pre-selected card). */
  readonly brandKit?: BrandKitDefaultOption | null;
  readonly brandKitLoading?: boolean;
  readonly documentType?: string | null;
  /**
   * Select the Brand Kit default (clears any explicit style/template). Distinct
   * from ``onSelect(undefined)`` only in intent — both resolve to "no explicit
   * pick" — but the card needs its own handler to read as a real radio option.
   */
  readonly onSelectBrandKit: () => void;
  /** True when the Brand Kit default is the active selection. */
  readonly brandKitSelected: boolean;
}

// ── Gallery card — a 16:9 slide-1 thumbnail + meta, as a radio option ──────
interface TemplateCardProps {
  readonly t: OoxmlTemplateOption;
  readonly selected: boolean;
  readonly disabled?: boolean;
  readonly onSelect: () => void;
}

const TemplateCard: React.FC<TemplateCardProps> = ({
  t,
  selected,
  disabled,
  onSelect,
}) => {
  const { i18n } = useLingui();
  const [imgLoaded, setImgLoaded] = React.useState(false);
  const [imgErrored, setImgErrored] = React.useState(false);
  const label = t.name || t.slug;
  // Surface the visual badges to screen readers (the chips are visual-only).
  const statusLabel = t.isBuiltin
    ? " — Ando"
    : t.scope === "org"
      ? i18n._(msg` — org`)
      : t.testOnly
        ? i18n._(msg` — test`)
        : "";
  const mergeLabel = t.mergeReady ? i18n._(msg` — pronto per il merge AI`) : "";
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={i18n._(msg`Template ${label}${statusLabel}${mergeLabel}`)}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        "group relative flex flex-col overflow-hidden rounded-xl border bg-white text-left transition-all",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange focus-visible:ring-offset-1",
        selected
          ? "border-ando-orange shadow-sm ring-2 ring-ando-orange"
          : "border-hairline hover:border-ando-orange/40 hover:shadow-sm",
        disabled && "pointer-events-none opacity-50",
      )}
    >
      {/* Thumbnail — fixed 16:9 box reserves space (no CLS) */}
      <div className="relative aspect-[16/9] w-full overflow-hidden bg-cream-deep/40">
        {t.thumbnailUrl && !imgErrored ? (
          <img
            src={t.thumbnailUrl}
            alt=""
            loading="lazy"
            decoding="async"
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgErrored(true)}
            className={cn(
              "h-full w-full object-cover transition-opacity duration-200",
              imgLoaded ? "opacity-100" : "opacity-0",
            )}
          />
        ) : null}
        {/* Loading shimmer / error fallback tile */}
        {!imgLoaded || imgErrored ? (
          <div className="absolute inset-0 grid place-items-center">
            {imgErrored ? (
              <span className="grid h-9 w-9 place-items-center rounded-md border border-hairline bg-cream/70 text-ink-3">
                {t.isBuiltin ? (
                  <AndoLogo size={18} />
                ) : (
                  <FileText className="h-4 w-4" strokeWidth={1.75} aria-hidden />
                )}
              </span>
            ) : (
              <span className="block h-full w-full animate-pulse bg-cream-deep/40" aria-hidden />
            )}
          </div>
        ) : null}
        {/* Selected check */}
        {selected ? (
          <span className="absolute top-1.5 right-1.5 grid h-5 w-5 place-items-center rounded-full bg-ando-orange text-white shadow">
            <Check className="h-3 w-3" strokeWidth={3} aria-hidden />
          </span>
        ) : null}
        {/* Top-left badges */}
        <div className="absolute top-1.5 left-1.5 flex gap-1">
          {t.isBuiltin ? (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-white/90 px-1.5 py-0.5 font-medium text-[9px] text-ando-orange uppercase tracking-wide shadow-sm">
              <AndoLogo size={9} /> Ando
            </span>
          ) : t.scope === "org" ? (
            <span className="rounded-full bg-white/90 px-1.5 py-0.5 font-medium text-[9px] text-ando-orange uppercase tracking-wide shadow-sm">
              {i18n._(msg`Org`)}
            </span>
          ) : null}
          {t.testOnly ? (
            <span className="rounded-full bg-amber-100/95 px-1.5 py-0.5 font-medium text-[9px] text-amber-700 uppercase tracking-wide shadow-sm">
              {i18n._(msg`test`)}
            </span>
          ) : null}
          {t.mergeReady ? (
            <span
              title={i18n._(msg`Pronto per il merge AI — il design del template è preservato, l'AI riempie solo il contenuto`)}
              className="inline-flex items-center gap-0.5 rounded-full bg-emerald-100/95 px-1.5 py-0.5 font-medium text-[9px] text-emerald-700 uppercase tracking-wide shadow-sm"
            >
              <Check className="h-2.5 w-2.5" strokeWidth={3} aria-hidden /> {i18n._(msg`AI-ready`)}
            </span>
          ) : null}
        </div>
      </div>
      {/* Meta */}
      <div className="flex flex-col gap-0.5 px-2.5 py-2">
        <span className="truncate font-medium text-[12px] text-ink-1">{label}</span>
        {t.description ? (
          <span className="line-clamp-2 text-[10.5px] text-ink-3 leading-snug">
            {t.description}
          </span>
        ) : null}
      </div>
    </button>
  );
};
TemplateCard.displayName = "StyleTemplatePicker.TemplateCard";

// ── Brand Kit default card — the user's default, FIRST + pre-selected ───────

interface BrandKitCardProps {
  readonly brandKit: BrandKitDefaultOption;
  readonly documentType?: string | null;
  readonly selected: boolean;
  readonly disabled?: boolean;
  readonly onSelect: () => void;
}

/** Italian label for the doc type the brand kit will brand (format match). */
const DOC_TYPE_LABEL: Record<string, MessageDescriptor> = {
  document: msg`Documento`,
  spreadsheet: msg`Foglio di calcolo`,
  presentation: msg`Presentazione`,
};

/**
 * The Brand Kit default template card. Mirrors {@link TemplateCard}'s shape
 * (16:9 tile + meta, ``role="radio"``) so it reads as part of the same gallery,
 * but it carries the "Predefinito" badge and is the ACTIVE pick by default. Its
 * tile is a {@link StyleSwatch}-style palette preview (the kit has no rendered
 * slide thumbnail) with a {@link FileType2} fallback when the palette is absent.
 */
const BrandKitCard: React.FC<BrandKitCardProps> = ({
  brandKit,
  documentType,
  selected,
  disabled,
  onSelect,
}) => {
  const { i18n } = useLingui();
  const hasPalette = Boolean(
    brandKit.accent && brandKit.ink && brandKit.surface,
  );
  const formatDescriptor = documentType ? DOC_TYPE_LABEL[documentType] : undefined;
  const formatLabel = formatDescriptor ? i18n._(formatDescriptor) : undefined;
  const subtitle = brandKit.hasTemplate
    ? i18n._(msg`Template del tuo Brand Kit`)
    : i18n._(msg`Colori e font del tuo Brand Kit`);
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={i18n._(msg`Brand Kit ${brandKit.name} — predefinito${formatLabel ? ` — ${formatLabel}` : ""}`)}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        "group relative flex min-h-[44px] flex-col overflow-hidden rounded-xl border bg-white text-left transition-all motion-reduce:transition-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange focus-visible:ring-offset-1",
        selected
          ? "border-ando-orange shadow-sm ring-2 ring-ando-orange"
          : "border-hairline hover:border-ando-orange/40 hover:shadow-sm",
        disabled && "pointer-events-none opacity-50",
      )}
    >
      {/* Palette-preview tile (the kit has no rendered slide thumbnail). */}
      <div
        className={cn(
          "relative grid aspect-[16/9] w-full place-items-center overflow-hidden",
          hasPalette ? "" : "bg-cream-deep/40",
        )}
        style={
          hasPalette ? { backgroundColor: toCssHex(brandKit.surface as string) } : undefined
        }
      >
        {hasPalette ? (
          <span aria-hidden className="flex w-[64%] flex-col gap-1.5">
            <span
              className="block h-2 w-[78%] rounded-full"
              style={{ backgroundColor: toCssHex(brandKit.ink as string), opacity: 0.85 }}
            />
            <span
              className="block h-1.5 w-full rounded-full"
              style={{ backgroundColor: toCssHex(brandKit.ink as string), opacity: 0.4 }}
            />
            <span
              className="block h-2 w-[40%] rounded-full"
              style={{ backgroundColor: toCssHex(brandKit.accent as string) }}
            />
          </span>
        ) : (
          <FileType2 className="h-7 w-7 text-ink-3" strokeWidth={1.5} aria-hidden />
        )}
        {/* Selected check */}
        {selected ? (
          <span className="absolute top-1.5 right-1.5 grid h-5 w-5 place-items-center rounded-full bg-ando-orange text-white shadow">
            <Check className="h-3 w-3" strokeWidth={3} aria-hidden />
          </span>
        ) : null}
        {/* Predefinito badge */}
        <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-0.5 rounded-full bg-ando-orange px-1.5 py-0.5 font-medium text-[9px] text-white uppercase tracking-wide shadow-sm">
          <AndoLogo size={9} /> {i18n._(msg`Predefinito`)}
        </span>
      </div>
      {/* Meta */}
      <div className="flex flex-col gap-0.5 px-2.5 py-2">
        <span className="truncate font-medium text-[12px] text-ink-1">
          {brandKit.name}
        </span>
        <span className="truncate text-[10.5px] text-ink-3 leading-snug">
          {formatLabel ? `${subtitle} · ${formatLabel}` : subtitle}
        </span>
      </div>
    </button>
  );
};
BrandKitCard.displayName = "StyleTemplatePicker.BrandKitCard";

const TemplateList: React.FC<TemplateListProps> = ({
  templates,
  loading,
  selectedSlug,
  disabled,
  onSelect,
  onOpenSettings,
  onUpload,
  uploadPending,
  brandKit,
  brandKitLoading,
  documentType,
  onSelectBrandKit,
  brandKitSelected,
}) => {
  const { i18n } = useLingui();
  const { ref, onKeyDown } = useRovingRadioGroup(disabled);
  const fileRef = React.useRef<HTMLInputElement>(null);

  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-2.5" aria-hidden>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="overflow-hidden rounded-xl border border-hairline">
            <div className="aspect-[16/9] animate-pulse bg-cream-deep/40" />
            <div className="space-y-1 px-2.5 py-2">
              <div className="h-2.5 w-2/3 animate-pulse rounded bg-cream-deep/50" />
              <div className="h-2 w-full animate-pulse rounded bg-cream-deep/40" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Curated builtins (the high-quality gallery, PRIMARY) first; uploads follow.
  const builtins = templates.filter((t) => t.isBuiltin);
  const uploads = templates.filter((t) => !t.isBuiltin);

  const uploadTile = onUpload ? (
    <>
      <input
        ref={fileRef}
        type="file"
        accept=".pptx,.docx,.xlsx"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onUpload(f);
          if (fileRef.current) fileRef.current.value = "";
        }}
      />
      <button
        type="button"
        disabled={disabled || uploadPending}
        onClick={() => fileRef.current?.click()}
        className="flex aspect-[16/9] flex-col items-center justify-center gap-1.5 rounded-xl border border-hairline border-dashed bg-white/40 text-ink-3 transition-colors hover:border-ando-orange/50 hover:bg-ando-orange-soft/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange disabled:opacity-50"
      >
        {uploadPending ? (
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
        ) : (
          <Upload className="h-5 w-5" strokeWidth={1.75} aria-hidden />
        )}
        <span className="font-medium text-[11px]">
          {uploadPending ? i18n._(msg`Preparazione AI…`) : i18n._(msg`Carica un template`)}
        </span>
      </button>
    </>
  ) : null;

  return (
    <div
      ref={ref}
      role="radiogroup"
      aria-label={i18n._(msg`Template del documento`)}
      onKeyDown={onKeyDown}
      className="flex max-h-[400px] flex-col gap-3 overflow-y-auto pr-0.5"
    >
      {/* The user's BRAND KIT default — FIRST + pre-selected. A skeleton while
          loading; the kit card when present; an empty-state CTA when the user
          has no kit at all. */}
      <section className="flex flex-col gap-1.5">
        <div className="px-1 font-medium text-[10px] text-ink-3 uppercase tracking-[0.06em]">
          {i18n._(msg`Il tuo Brand Kit`)}
        </div>
        {brandKitLoading ? (
          <div
            className="grid grid-cols-2 gap-2.5"
            aria-hidden
            aria-busy="true"
          >
            <div className="overflow-hidden rounded-xl border border-hairline">
              <div className="aspect-[16/9] animate-pulse bg-cream-deep/40 motion-reduce:animate-none" />
              <div className="space-y-1 px-2.5 py-2">
                <div className="h-2.5 w-2/3 animate-pulse rounded bg-cream-deep/50 motion-reduce:animate-none" />
                <div className="h-2 w-full animate-pulse rounded bg-cream-deep/40 motion-reduce:animate-none" />
              </div>
            </div>
          </div>
        ) : brandKit ? (
          <div className="grid grid-cols-2 gap-2.5">
            <BrandKitCard
              brandKit={brandKit}
              documentType={documentType}
              selected={brandKitSelected}
              disabled={disabled}
              onSelect={onSelectBrandKit}
            />
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 rounded-lg border border-hairline border-dashed bg-white/40 px-4 py-5 text-center">
            <FileType2 className="h-5 w-5 text-ink-3" strokeWidth={1.5} aria-hidden />
            <p className="max-w-[34ch] text-[11px] text-ink-3 leading-snug">
              {i18n._(msg`Nessun template nel Brand Kit — caricane uno.`)}
            </p>
            {onOpenSettings ? (
              <button
                type="button"
                onClick={onOpenSettings}
                disabled={disabled}
                className="mt-0.5 rounded-md border border-ando-orange/60 bg-card px-2.5 py-1 font-medium text-[11.5px] text-ando-orange transition-colors hover:bg-ando-orange-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange disabled:opacity-50 motion-reduce:transition-none"
              >
                {i18n._(msg`Apri il Brand Kit`)}
              </button>
            ) : null}
          </div>
        )}
      </section>

      {builtins.length > 0 ? (
        <section className="flex flex-col gap-1.5">
          <div className="px-1 font-medium text-[10px] text-ink-3 uppercase tracking-[0.06em]">
            {i18n._(msg`Template Ando`)}
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            {builtins.map((t) => (
              <TemplateCard
                key={`builtin:${t.slug}`}
                t={t}
                selected={selectedSlug === t.slug}
                disabled={disabled}
                onSelect={() => onSelect(t.slug)}
              />
            ))}
          </div>
        </section>
      ) : null}

      {uploads.length > 0 || uploadTile ? (
        <section className="flex flex-col gap-1.5">
          <div className="px-1 font-medium text-[10px] text-ink-3 uppercase tracking-[0.06em]">
            {i18n._(msg`I tuoi template`)}
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            {uploads.map((t) => (
              <TemplateCard
                key={`${t.scope ?? "user"}:${t.slug}`}
                t={t}
                selected={selectedSlug === t.slug}
                disabled={disabled}
                onSelect={() => onSelect(t.slug)}
              />
            ))}
            {uploadTile}
          </div>
        </section>
      ) : null}

      {/* Nothing at all (no builtins, no uploads, no inline upload). The
          Brand Kit section above already renders its own empty-state CTA, so
          only fall back to this when a brand kit IS present (or loading). */}
      {builtins.length === 0 &&
      uploads.length === 0 &&
      !uploadTile &&
      (brandKit || brandKitLoading) ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-hairline border-dashed bg-white/40 px-4 py-5 text-center">
          <Upload className="h-5 w-5 text-ink-3" strokeWidth={1.75} aria-hidden />
          <p className="max-w-[34ch] text-[11px] text-ink-3 leading-snug">
            {i18n._(msg`Carica un template brandizzato dalle impostazioni per riusarlo qui.`)}
          </p>
          {onOpenSettings ? (
            <button
              type="button"
              onClick={onOpenSettings}
              disabled={disabled}
              className="mt-0.5 rounded-md border border-ando-orange/60 bg-card px-2.5 py-1 font-medium text-[11.5px] text-ando-orange transition-colors hover:bg-ando-orange-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange disabled:opacity-50"
            >
              {i18n._(msg`Apri impostazioni`)}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
TemplateList.displayName = "StyleTemplatePicker.TemplateList";

// ── Main component ─────────────────────────────────────────────────────────

export const StyleTemplatePicker: React.FC<StyleTemplatePickerProps> = ({
  value,
  onChange,
  styles,
  templates,
  brandKit,
  brandKitLoading,
  stylesLoading,
  templatesLoading,
  documentType,
  onOpenTemplateSettings,
  onUploadTemplate,
  uploadTemplatePending,
  onLearnStyle,
  learnStylePending,
  learnStyleError,
  disabled,
  className,
}) => {
  const { i18n } = useLingui();
  // Templates are filtered to the doc type (styles work for any doc type).
  // A template without a declared ``documentType`` is treated as universal.
  const visibleTemplates = React.useMemo(() => {
    if (!documentType) return templates;
    return templates.filter(
      (t) => !t.documentType || t.documentType === documentType,
    );
  }, [templates, documentType]);

  // The Brand Kit default is the active pick whenever the user has made NO
  // explicit choice (no style, no template) — it maps to the BE's "apply the
  // user's kit" default (emit nothing). Surfacing it pre-selected requires the
  // Template tab to be the default landing tab (where the card lives) unless the
  // user already picked a style.
  const brandKitSelected = !value.styleId && !value.templateSlug;

  // Jul 11, 2026 — STILE is the primary choice: the from-scratch design system
  // (smart-slide patterns + adaptive tokens + native charts) produces
  // master-grade decks for every style preset, so the simple default flow is
  // "pick a mood, one click". The Template tab remains for users who bring
  // THEIR OWN corporate file (fidelity path). A previously chosen template
  // still opens on Template. Controlled so picking in one tab (which clears
  // the other) keeps the user where expected.
  const [tab, setTab] = React.useState<"style" | "template">(
    value.templateSlug ? "template" : "style",
  );
  React.useEffect(() => {
    if (value.templateSlug) setTab("template");
  }, [value.templateSlug]);

  const handlePickStyle = React.useCallback(
    (styleId?: string) => {
      // Style and template are mutually exclusive — picking a style clears
      // any template slug.
      onChange({ styleId, templateSlug: undefined });
    },
    [onChange],
  );

  const handlePickTemplate = React.useCallback(
    (slug?: string) => {
      onChange({ styleId: undefined, templateSlug: slug });
    },
    [onChange],
  );

  return (
    <section
      aria-labelledby="style-template-picker-title"
      className={cn("flex flex-col gap-2.5", className)}
    >
      <div className="flex items-center gap-1.5">
        <AndoLogo size={14} />
        <h3
          id="style-template-picker-title"
          className="font-medium text-[11px] text-ink-2 uppercase tracking-[0.06em]"
        >
          {i18n._(msg`Aspetto del documento`)}
        </h3>
      </div>

      <Tabs
        value={tab}
        onValueChange={(v) => setTab(v === "template" ? "template" : "style")}
      >
        <TabsList className="w-full">
          <TabsTrigger value="style" className="flex-1" disabled={disabled}>
            {i18n._(msg`Stile`)}
          </TabsTrigger>
          <TabsTrigger value="template" className="flex-1" disabled={disabled}>
            {i18n._(msg`Template`)}
            {visibleTemplates.length > 0 ? (
              <span className="ml-1.5 rounded-full bg-ink-3/15 px-1.5 text-[10px] text-ink-2">
                {visibleTemplates.length}
              </span>
            ) : null}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="style" className="mt-2.5">
          <StyleList
            styles={styles}
            loading={stylesLoading}
            selectedId={value.styleId}
            disabled={disabled}
            onSelect={handlePickStyle}
            onLearnStyle={onLearnStyle}
            learnStylePending={learnStylePending}
            learnStyleError={learnStyleError}
            brandKit={brandKit}
          />
        </TabsContent>

        <TabsContent value="template" className="mt-2.5">
          <TemplateList
            templates={visibleTemplates}
            loading={templatesLoading}
            selectedSlug={value.templateSlug}
            disabled={disabled}
            onSelect={handlePickTemplate}
            onOpenSettings={onOpenTemplateSettings}
            onUpload={onUploadTemplate}
            uploadPending={uploadTemplatePending}
            brandKit={brandKit}
            brandKitLoading={brandKitLoading}
            documentType={documentType}
            brandKitSelected={brandKitSelected}
            onSelectBrandKit={() => handlePickTemplate(undefined)}
          />
        </TabsContent>
      </Tabs>
    </section>
  );
};
StyleTemplatePicker.displayName = "StyleTemplatePicker";
